import { Fragment, ReactNode, Suspense, lazy } from 'react';
import { RouterProvider } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import router from './router/router';
import { ConfigProvider, theme as antdThemeConfig } from 'antd';
import { AppErrorFallback } from './components/ErrorBoundary/AppErrorFallback';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SuperTokensWrapper } from 'supertokens-auth-react';
import { ModalProvider } from './context/ModalContext';
import { queryClient } from './queryClient';
import { useThemeStore } from './stores/themeStore';
import { lightColors, darkColors } from './constants/design_system/colors';

// Dev-only @react-buddy palette. In production the ternary's dev branch is
// statically false, so Rollup drops the dynamic import — @react-buddy and the
// ComponentPreviews chunk never reach the prod bundle. In prod it's a passthrough.
const DevSupportWrapper = import.meta.env.DEV
  ? lazy(() => import('./dev/DevSupportWrapper'))
  : ({ children }: { children: ReactNode }) => <Fragment>{children}</Fragment>;

export default function App() {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const colors = resolvedTheme === 'dark' ? darkColors : lightColors;

  const antdTheme = {
    algorithm: resolvedTheme === 'dark' ? antdThemeConfig.darkAlgorithm : antdThemeConfig.defaultAlgorithm,
    token: {
      colorPrimary: colors.taskonioGreen[600],
      fontFamily: 'Figtree',
      ...(resolvedTheme === 'dark'
        ? {
            colorBgBase: colors.brand.white,
            colorBgLayout: colors.brand.white,
            colorBgContainer: colors.neutral[100],
            colorBgElevated: colors.neutral[200],
            colorBorder: colors.neutral[400],
            colorBorderSecondary: colors.neutral[300],
            colorText: colors.brand['grey-dark'],
            colorTextSecondary: colors.neutral[900],
            colorTextTertiary: colors.neutral[800],
            colorTextQuaternary: colors.neutral[700],
            colorFill: colors.neutral[300],
            colorFillSecondary: colors.neutral[200],
            colorFillTertiary: colors.neutral[100],
            colorFillQuaternary: colors.brand.white,
          }
        : {}),
    },
    components: {
      Button: {
        colorPrimary: colors.taskonioGreen[600],
        algorithm: true,
      },
      Switch: {
        colorPrimary: colors.blue[600],
        colorPrimaryHover: colors.blue[500],
      },
      Radio: {
        colorPrimary: colors.blue[600],
        algorithm: true,
      },
      Tabs: {},
      Layout: {
        lightSiderBg: colors.brand['grey-light'],
        bodyBg: colors.brand.white,
      },
      Menu: {
        horizontalItemHoverColor: colors.taskonioGreen[600],
        horizontalItemSelectedColor: colors.taskonioGreen[600],
      },
      DatePicker: {
        colorPrimary: colors.blue[600],
      },
    },
  };

  return (
    <SuperTokensWrapper>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={antdTheme}>
          <ModalProvider>
            <Suspense fallback={null}>
              <DevSupportWrapper>
                <Sentry.ErrorBoundary fallback={({ resetError }) => <AppErrorFallback resetError={resetError} />}>
                  <RouterProvider router={router} />
                </Sentry.ErrorBoundary>

                {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
              </DevSupportWrapper>
            </Suspense>
          </ModalProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </SuperTokensWrapper>
  );
}
